<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { frontendURL } from 'dashboard/helper/URLHelper';
import Icon from 'dashboard/components-next/icon/Icon.vue';

// CRM-04 R2-T7 (§6.1): as conversas do negócio que a pessoa vê, cada uma com a caixa, o contato, o
// status e o responsável. As de caixas que ela não vê o servidor nem manda: aparecem só como contagem.
const props = defineProps({
  conversations: { type: Array, default: () => [] },
  contacts: { type: Array, default: () => [] },
  hiddenCount: { type: Number, default: 0 },
});

const { t } = useI18n();
const route = useRoute();

const nomeDoContato = contactId =>
  props.contacts.find(contato => contato.id === contactId)?.name || '';

// O status de UMA conversa, no singular. As chaves do filtro da lista (`CHAT_STATUS_FILTER_ITEMS`) são
// plurais em pt_BR ("Abertas"), e ao lado de uma conversa só liam errado.
const rotuloDoStatus = status =>
  ({
    open: t('KANBAN.DRAWER.CONVERSATION_STATUS.open'),
    pending: t('KANBAN.DRAWER.CONVERSATION_STATUS.pending'),
    snoozed: t('KANBAN.DRAWER.CONVERSATION_STATUS.snoozed'),
    resolved: t('KANBAN.DRAWER.CONVERSATION_STATUS.resolved'),
  })[status] || '';

const linhas = computed(() =>
  props.conversations.map(conversa => ({
    ...conversa,
    contato: nomeDoContato(conversa.contact_id),
    status: rotuloDoStatus(conversa.status),
    responsavel: conversa.assignee?.available_name || conversa.assignee?.name,
    // A rota da conversa usa o display_id, não a chave primária.
    url: frontendURL(
      `accounts/${route.params.accountId}/conversations/${conversa.display_id}`
    ),
  }))
);
</script>

<template>
  <section class="flex flex-col gap-2">
    <h3 class="text-xs font-medium uppercase text-n-slate-10">
      {{ $t('KANBAN.DRAWER.CONVERSATIONS_SECTION') }}
    </h3>
    <router-link
      v-for="conversa in linhas"
      :key="conversa.id"
      :to="conversa.url"
      data-conversa
      class="flex items-center gap-2 p-2 text-xs rounded-lg bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2"
    >
      <Icon icon="i-lucide-message-circle" class="flex-shrink-0 size-3.5" />
      <span class="flex flex-col flex-1 min-w-0">
        <span class="truncate text-n-slate-12">
          {{ conversa.inbox?.name }}
          <template v-if="conversa.contato">
            {{
              $t('KANBAN.DRAWER.CONVERSATION_CONTACT', {
                name: conversa.contato,
              })
            }}
          </template>
        </span>
        <span v-if="conversa.responsavel" class="truncate">
          {{
            $t('KANBAN.DRAWER.CONVERSATION_ASSIGNEE', {
              name: conversa.responsavel,
            })
          }}
        </span>
        <!-- §6.2: as etiquetas saíram da face e ficam aqui, por conversa. -->
        <span
          v-if="conversa.labels?.length"
          data-etiquetas
          :aria-label="$t('KANBAN.DRAWER.CONVERSATION_LABELS')"
          class="flex flex-wrap gap-1 pt-0.5"
        >
          <span
            v-for="etiqueta in conversa.labels"
            :key="etiqueta"
            class="px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11"
          >
            {{ etiqueta }}
          </span>
        </span>
      </span>
      <!-- N10: as não lidas saíram da face e ficam aqui, por conversa. -->
      <span
        v-if="conversa.unread_count"
        data-nao-lidas
        class="flex-shrink-0 px-1.5 py-0.5 font-medium text-white rounded-full bg-n-brand tabular-nums"
      >
        {{ conversa.unread_count }}
      </span>
      <span
        v-if="conversa.status"
        class="flex-shrink-0 px-1.5 py-0.5 rounded bg-n-alpha-2"
      >
        {{ conversa.status }}
      </span>
    </router-link>
    <p
      v-if="hiddenCount"
      data-ocultas
      class="flex items-center gap-1.5 text-xs text-n-slate-10"
    >
      <Icon icon="i-lucide-lock" class="flex-shrink-0 size-3.5" />
      {{
        $t(
          'KANBAN.DRAWER.HIDDEN_CONVERSATIONS',
          { count: hiddenCount },
          hiddenCount
        )
      }}
    </p>
  </section>
</template>
